<template>
    <v-container fluid class="pa-4 bg-grey-lighten-5 h-100">
        <div class="d-flex justify-space-between align-center mb-4">
            <div class="py-1">
                <h2 class="text-h5 font-weight-bold text-grey-darken-3">법령 관리</h2>
            </div>
            <div class="d-flex align-center">
                <v-btn color="indigo-darken-2" prepend-icon="mdi-content-save" class="mr-1 rounded-lg" elevation="2"
                    @click="saveChanges" :loading="saving" :disabled="saving || loading" density="compact">
                    저장
                </v-btn>
            </div>
        </div>

        <v-row v-if="loading">
            <v-col cols="12" class="text-center pa-4">
                <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
            </v-col>
        </v-row>

        <v-row v-else class="ga-1">
            <v-col v-for="part in legislations" :key="part.lawPartCd" cols="12" sm="6" md="4" lg="3" class="pa-1">
                <v-card class="rounded-lg border-none shadow-soft h-100 law-part-card" elevation="0" color="white">
                    <v-card-title class="px-2 py-0.5 bg-indigo-lighten-5 d-flex align-center">
                        <v-icon color="indigo-darken-2" class="mr-1" size="16">mdi-folder-text-outline</v-icon>
                        <span class="text-caption font-weight-bold text-indigo-darken-4 text-truncate"
                            :title="part.lawPartNm">{{ part.lawPartNm }}</span>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-list density="compact" class="pa-0 bg-transparent">
                        <v-list-item v-for="chap in part.chapterList" :key="chap.lawChapCd"
                            :class="['chap-item', chap.useYn === 'Y' ? 'chap-active' : 'chap-inactive']"
                            density="compact">
                            <template v-slot:prepend>
                                <v-checkbox-btn :model-value="chap.useYn === 'Y'" :disabled="saving"
                                    @update:model-value="val => toggleUseYn(chap, val)" color="indigo" class="mr-0"
                                    density="compact"></v-checkbox-btn>
                            </template>
                            <v-list-item-title class="text-caption chap-title font-weight-medium">{{ chap.lawChapNm
                            }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>
            <v-col v-if="legislations.length === 0" cols="12" class="text-center pa-8">
                <v-icon size="32" color="grey-lighten-1">mdi-database-outline</v-icon>
                <p class="text-caption text-grey-darken-1 mt-1">데이터가 없습니다.</p>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';

const loading = ref(false);
const saving = ref(false);
const legislations = ref([]);

const toggleUseYn = (chap, val) => {
    chap.useYn = val ? 'Y' : 'N';
};

const loadLawTree = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/law/list');
        if (response.data.status === 'success') {
            legislations.value = response.data.data.list;
        }
    } catch (error) {
        console.error('Failed to load law tree:', error);
    } finally {
        loading.value = false;
    }
};

const saveChanges = async () => {
    if (saving.value) return;
    saving.value = true;
    try {
        const allChapters = legislations.value.reduce((acc, part) => {
            return acc.concat(part.chapterList);
        }, []);

        const response = await axios.post('/law/save', allChapters);
        if (response.data.status === 'success') {
            alert(response.data.message);
            await loadLawTree();
        } else {
            const failures = response.data.data?.result?.failures || [];
            const details = failures.slice(0, 5).map(failure => `${failure.lawId}: ${failure.message}`).join('\n');
            alert(response.data.message + (details ? '\n' + details : ''));
        }
    } catch (error) {
        console.error('Failed to save law changes:', error);
        alert('서버와 통신 중 오류가 발생했습니다.');
    } finally {
        saving.value = false;
    }
};

onMounted(() => {
    loadLawTree();
});
</script>

<style scoped>
.shadow-soft {
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.05) !important;
}

.law-part-card {
    transition: transform 0.1s ease-in-out;
}

.law-part-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08) !important;
}

.chap-item {
    border-radius: 4px !important;
    margin-bottom: -4px !important;
    min-height: 20px !important;
}

.chap-active {
    color: #1a237e !important;
}

.chap-inactive {
    color: #757575 !important;
    opacity: 0.8;
}

.chap-title {
    white-space: normal !important;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.0 !important;
    font-size: 0.72rem !important;
}

/* Vuetify overrides for maximum compactness */
:deep(.v-list-item__prepend) {
    margin-right: -4px !important;
}

:deep(.v-checkbox-btn .v-selection-control) {
    min-height: 20px !important;
}

/* Remove vertical scrolls */
:deep(.v-list) {
    overflow: visible !important;
}

:deep(.v-virtual-scroll) {
    overflow: visible !important;
}
</style>
